import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { useI18n } from "@wato/i18n/react";
import { buttonVariantClasses, cx, DismissibleBanner } from "@wato/ui";
import { useApi } from "../../../api/context";
import { SHELL_STALE_MS } from "../../../api/query-client";
import type { ChannelHealth } from "../../../api/types";
import { useWorkspace } from "../../workspace/WorkspaceContext";
import { canFixChannels, classifyChannel, type ChannelProblem } from "./channel-problems";

/**
 * FB2-51: shell banner for channels that need attention (disconnected, token expired, no webhook
 * for a while while "active", QR session failed/stopped). Shown only to users who can fix channels.
 * G10: a single dismissible line in the shell's `BannerSlot`; the dismissal is remembered per problem.
 */
export function ChannelHealthBanner() {
  const { t } = useI18n();
  const api = useApi();
  const { workspace } = useWorkspace();
  const allowed = canFixChannels(workspace.permissions);

  const query = useQuery({
    queryKey: ["channel-problems", workspace.id],
    enabled: allowed,
    staleTime: SHELL_STALE_MS,
    refetchInterval: 5 * 60_000,
    queryFn: async (): Promise<ChannelProblem[]> => {
      const channels = await api.listChannels(workspace.id);
      const found = await Promise.all(
        channels.map(async (channel) => {
          let health: ChannelHealth | null = null;
          if (
            channel.provider === "whatsapp_cloud" &&
            channel.status.toLowerCase() === "connected"
          ) {
            health = await api.getChannelHealth(workspace.id, channel.id).catch(() => null);
          }
          return classifyChannel(channel, health);
        }),
      );
      return found.filter((p): p is ChannelProblem => p !== null);
    },
  });

  const problems = query.data ?? [];
  if (!allowed || problems.length === 0) return null;
  // Q4-ONB-01: one banner per problem kind (not one per channel), so two quiet channels do not
  // stack two identical alerts.
  const groups = [...new Set(problems.map((p) => p.reason))].map((reason) => ({
    reason,
    channelIds: problems.filter((p) => p.reason === reason).map((p) => p.channelId),
  }));
  const channelsPath = `/w/${workspace.id}/channels`;
  // G10: one banner at a time; further problem kinds are listed on the channels page itself.
  const problem = groups[0];
  if (!problem) return null;
  return (
    <DismissibleBanner
      priority={10}
      id={`channel-health:${problem.reason}:${[...problem.channelIds].sort().join(",")}`}
      severity={problem.reason === "webhookSilent" ? "warning" : "danger"}
      testId="channel-health-banner"
      action={
        <Link
          to={channelsPath}
          className={cx(
            "inline-flex h-8 items-center rounded-md px-3 text-sm font-medium",
            buttonVariantClasses.secondary,
          )}
        >
          {t(
            problem.reason === "webhookSilent" ||
              problem.reason === "threadOwnerConflict" ||
              problem.reason.startsWith("qr")
              ? "channels.healthBanner.cta.open"
              : "channels.healthBanner.cta.reconnect",
          )}
        </Link>
      }
    >
      {t(`channels.healthBanner.reason.${problem.reason}`)}
      {problem.channelIds.length > 1 && (
        <> ({t("channels.healthBanner.channelsCount", { count: problem.channelIds.length })})</>
      )}
    </DismissibleBanner>
  );
}
